import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSettings } from '../../contexts/SettingsContext';
// Import all data hooks directly to ensure real-time data
import { 
  useFinancialData, 
  useReservationsData, 
  useClientsData, 
  usePackagesData,
  useDocumentsData,
  useSuppliersData
} from '../../services/dataService';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Fade,
  Grow,
  Slide,
  ButtonBase,
  useTheme,
  useMediaQuery,
  Skeleton,
  IconButton,
  Stack,
  Tooltip,
  AvatarGroup,
  Badge,
  Zoom,
  CircularProgress,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  People,
  Flight,
  Euro,
  TrendingUp,
  CalendarToday,
  Notifications,
  CheckCircle,
  Warning,
  Error,
  Schedule,
  Add,
  Visibility,
  Assessment,
  Business,
  Description,
  ArrowForward,
  AttachMoney,
  Today,
  Group,
  Star,
  Speed,
  Timeline,
  BarChart,
  PieChart,
  ShowChart,
  TrendingDown,
  AccountBalance,
  LocationOn,
  Public,
  LocalActivity,
  Favorite,
  BookmarkBorder,
  Brightness1,
  PlayArrow,
  MoreVert,
  FilterList,
  Refresh,
  CloudDone,
  Wifi,
  BatteryFull,
  SignalWifi4Bar,
  WbSunny,
  Thermostat,
  Air,
  MonetizationOn,
  FlightTakeoff,
  FlightLand,
  Hotel,
  Restaurant,
  DirectionsCar,
  CameraAlt,
  ShareLocation,
  Explore,
  Language,
  CardGiftcard,
  CompareArrows,
  Analytics,
  RocketLaunch,
  AutoAwesome,
  LocalFireDepartment,
  ElectricBolt,
  Diamond,
  Whatshot,
  Celebration
} from '@mui/icons-material';

// Custom Chart Components
// Replace AnimatedChart component to directly display data without animation or hover effects
const AnimatedChart = ({ data, color, height = 60 }) => {
  if (!data || data.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          height, 
          color: 'rgba(255,255,255,0.7)',
          fontSize: '0.75rem'
        }}
      >
        Aucune donnée
      </Box>
    );
  }

  return (
    <Box 
      className="dashboard-chart-container"
      sx={{ 
        display: 'flex', 
        alignItems: 'end', 
        height, 
        gap: 0.5,
        opacity: 1
      }}
    >
      {data.map((value, index) => (
        <Box
          key={`chart-bar-${index}`}
          sx={{
            flex: 1,
            height: `${(value / Math.max(...data)) * 100}%`,
            background: `linear-gradient(to top, ${color}80, ${color})`,
            borderRadius: '2px 2px 0 0'
          }}
        />
      ))}
    </Box>
  );
};

// Update text color in GlowingMetric to ensure visibility and remove animation
const GlowingMetric = ({ label, value, icon, color, trend, children }) => (
  <Box sx={{ 
    position: 'relative',
    p: 2,
    borderRadius: 3,
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${color}20, transparent)`,
      zIndex: 0
    }
  }}>
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ 
          p: 1,
          borderRadius: 2,
          background: `${color}20`,
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        {trend && (
          <Chip 
            label={trend} 
            size="small" 
            sx={{ 
              backgroundColor: trend.startsWith('+') ? '#059669' : '#dc2626',
              color: 'white',
              fontWeight: 600
            }}
          />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'rgba(51,65,85,0.9)', mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(51,65,85,0.8)' }}>
        {label}
      </Typography>
      <Box className="dashboard-chart-container" sx={{ mt: 1.5 }}>
      {children}
      </Box>
    </Box>
  </Box>
);

// Fix FloatingActionCard to use a better contrasted background without hover effects
const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
  // Get theme and check if mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card sx={{
    background: `linear-gradient(135deg, ${color}15, white)`,
      border: `1px solid ${color}30`,
      borderRadius: { xs: 3, md: 4 },
      overflow: 'hidden',
      position: 'relative',
      cursor: 'pointer',
      height: '100%', // Ensure consistent height
    }} onClick={onClick}>
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5, md: 3 }, 
        position: 'relative', 
        zIndex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, md: 2 }, 
          mb: { xs: 1.5, md: 2 },
          flexWrap: 'wrap'
        }}>
          <Avatar className="action-icon" sx={{ 
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            width: { xs: 48, sm: 52, md: 56 },
            height: { xs: 48, sm: 52, md: 56 },
            boxShadow: `0 8px 25px ${color}40`,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: 26, sm: 28, md: 30 }
            }
          }}>
            {icon}
          </Avatar>
          {badge && (
            <Chip 
              label={badge} 
              size="small" 
              sx={{ 
                background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                color: 'white',
                fontWeight: 600,
                height: { xs: '20px', md: '24px' },
                fontSize: { xs: '0.7rem', md: '0.75rem' }
              }}
            />
          )}
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700, 
            mb: { xs: 0.5, md: 1 }, 
            color: 'rgba(51,65,85,0.9)',
            lineHeight: 1.3,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(51,65,85,0.7)',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            display: { xs: '-webkit-box', md: 'block' },
            WebkitLineClamp: { xs: 2, md: 'none' },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
);
};

// Fix PulsingDot to remove animation
const PulsingDot = ({ color, size = 8 }) => (
  <Box sx={{ position: 'relative', display: 'inline-block' }}>
    <Box sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color
    }} />
  </Box>
);

// Define SkeletonCard component for progressive loading
const SkeletonCard = ({ height = 120 }) => (
  <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 3, width: '100%' }} />
);

// Define SkeletonStat component for stats cards
const SkeletonStat = () => (
  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Skeleton width={80} height={16} sx={{ mb: 0.5 }} />
          <Skeleton width={120} height={32} />
          <Skeleton width={100} height={16} sx={{ mt: 0.5 }} />
          <Skeleton width={60} height={24} sx={{ mt: 1, borderRadius: 10 }} />
        </Box>
        <Skeleton variant="circular" width={48} height={48} />
      </Box>
      <Skeleton height={6} sx={{ mt: 1, borderRadius: 3 }} />
    </CardContent>
  </Card>
);

// Completely redesigned Dashboard component to directly use real-time data from all sources
const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { settings } = useSettings();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isXsMobile = useMediaQuery('(max-width:400px)');
  
  // Direct access to all data sources to ensure real-time updates
  const { transactions = [], stats: financialStats, loading: financialLoading } = useFinancialData();
  const { reservations = [], stats: reservationsStats, loading: reservationsLoading } = useReservationsData();
  const { clients = [], stats: clientsStats, loading: clientsLoading } = useClientsData();
  const { packages = [], stats: packagesStats, loading: packagesLoading } = usePackagesData();
  const { suppliers = [], loading: suppliersLoading } = useSuppliersData();
  const { documents = [], loading: documentsLoading } = useDocumentsData();
  
  // Chargement optimisé - affichage immédiat du dashboard
  const isLoading = false;
  
  // Define a function to render skeleton or real content
  const renderWithSkeleton = (isLoading, content, skeletonComponent) => {
    return isLoading ? skeletonComponent : content;
  };
  
  // State for dashboard-specific data
  const [activeTab, setActiveTab] = useState(0);
  const [weatherData, setWeatherData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [error, setError] = useState(null);
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  

    
  // Periodic refresh effect - forces a UI update every 5 seconds
  useEffect(() => {
    // Function to force UI refresh
    const forceRefresh = () => {
      if (isMounted.current) {
        setLastRefresh(Date.now());
      }
    };
    
    // Set up interval for periodic refreshes (every 5 seconds)
    const refreshInterval = setInterval(forceRefresh, 5000);
    
    // Clean up on unmount
    return () => {
      isMounted.current = false;
      clearInterval(refreshInterval);
    };
  }, []);
  
  // Effect to handle component mount/unmount
  useEffect(() => {
    // Force initial data refresh on mount
    window.dispatchEvent(new CustomEvent('globalDataRefresh', {
      detail: { source: 'dashboard', requestType: 'full_refresh', timestamp: Date.now() }
    }));
    
    // Clean up function
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // We'll use progressive loading instead of a full loading screen
  
  // Show error state if there was a problem
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur de chargement des données: {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.dispatchEvent(new CustomEvent('globalDataRefresh', {
            detail: { type: 'error_retry', source: 'dashboard', timestamp: Date.now() }
          }))}
        >
          Réessayer
        </Button>
      </Box>
    );
  }
  
  // Enhanced stats object with comprehensive analytics from real-time data
  const stats = {
    // Basic metrics
    clients: (clients && Array.isArray(clients)) ? clients.length : 0,
    reservations: (reservations && Array.isArray(reservations)) ? reservations.length : 0,
    revenue: financialStats ? (financialStats.totalRevenue || 0).toLocaleString() + ' DZD' : '0 DZD',
    packages: (packages && Array.isArray(packages)) ? packages.length : 0,
    suppliers: (suppliers && Array.isArray(suppliers)) ? suppliers.length : 0,
    documents: (documents && Array.isArray(documents)) ? documents.length : 0,
    
    // Financial Analytics
    totalRevenue: (transactions && Array.isArray(transactions)) ? 
      transactions
        .filter(t => t.type === 'Recette')
        .reduce((sum, t) => sum + (t.montant || 0), 0) : 0,
    totalExpenses: (transactions && Array.isArray(transactions)) ? 
      transactions
        .filter(t => t.type === 'Dépense')
        .reduce((sum, t) => sum + (t.montant || 0), 0) : 0,
    netProfit: (transactions && Array.isArray(transactions)) ? 
      transactions.reduce((sum, t) => sum + (t.type === 'Recette' ? (t.montant || 0) : -(t.montant || 0)), 0) : 0,
    
    // Reservation Analytics
    confirmedReservations: (reservations && Array.isArray(reservations)) ? 
      reservations.filter(r => r.statut === 'Confirmée' || r.statutPaiement === 'Payé').length : 0,
    pendingReservations: (reservations && Array.isArray(reservations)) ? 
      reservations.filter(r => r.statut === 'En attente' || r.statutPaiement === 'En attente').length : 0,
    completedReservations: (reservations && Array.isArray(reservations)) ? 
      reservations.filter(r => new Date(r.dateRetour || r.dateFin) < new Date()).length : 0,
    
    // Average booking value
    averageBookingValue: (reservations && Array.isArray(reservations) && reservations.length > 0) ? 
      reservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0) / reservations.length : 0,
    
    // Monthly trends (last 6 months)
    monthlyReservations: (() => {
      if (!reservations || !Array.isArray(reservations)) return [];
      const months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = monthDate.toLocaleDateString('fr-FR', { month: 'short' });
        const count = reservations.filter(r => {
          const resDate = new Date(r.dateReservation || r.dateDepart);
          return resDate.getMonth() === monthDate.getMonth() && 
                 resDate.getFullYear() === monthDate.getFullYear();
        }).length;
        months.push({ month: monthName, count });
      }
      return months;
    })(),
    
    // Monthly revenue trends (last 6 months)
    monthlyRevenue: (() => {
      if (!transactions || !Array.isArray(transactions)) return [];
      const months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = monthDate.toLocaleDateString('fr-FR', { month: 'short' });
        const revenue = transactions.filter(t => {
          const transDate = new Date(t.date);
          return t.type === 'Recette' && 
                 transDate.getMonth() === monthDate.getMonth() && 
                 transDate.getFullYear() === monthDate.getFullYear();
        }).reduce((sum, t) => sum + (t.montant || 0), 0);
        months.push({ month: monthName, revenue: revenue / 1000 }); // Convert to thousands
      }
      return months;
    })(),
    
    // Client distribution by status
    clientDistribution: (() => {
      if (!clients || !Array.isArray(clients)) return [];
      const distribution = clients.reduce((acc, client) => {
        const status = client.statut || 'Actif';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      return Object.entries(distribution).map(([status, count]) => ({ status, count }));
    })(),
    
    // Package performance
    packagePerformance: (() => {
      if (!packages || !Array.isArray(packages) || !reservations || !Array.isArray(reservations)) return [];
      return packages.map(pkg => {
        const bookings = reservations.filter(r => r.forfaitId === pkg.id || 
          r.destination === pkg.destination).length;
        const revenue = reservations
          .filter(r => r.forfaitId === pkg.id || r.destination === pkg.destination)
          .reduce((sum, r) => sum + (r.prixTotal || 0), 0);
        return {
          name: pkg.titre || pkg.destination || 'Package',
          bookings,
          revenue: revenue / 1000, // Convert to thousands
          price: pkg.prix || 0
        };
      }).sort((a, b) => b.bookings - a.bookings).slice(0, 5);
    })(),
    
    // Conversion rate (confirmed vs total reservations)
    conversionRate: (reservations && Array.isArray(reservations) && reservations.length > 0) ? 
      (reservations.filter(r => r.statut === 'Confirmée').length / reservations.length * 100).toFixed(1) : 0,
    
    // Generate recent activity from real transactions only
    recentActivity: (transactions && Array.isArray(transactions) && transactions.length > 0) ? 
      transactions.slice(0, 4).map((transaction, index) => ({
        id: transaction.id || index,
        type: transaction.type === 'Recette' ? 'payment' : 'alert',
        title: transaction.type === 'Recette' ? 'Paiement reçu' : 'Nouvelle dépense',
        description: transaction.description || `${transaction.montant?.toLocaleString()} DZD ${transaction.type === 'Recette' ? 'reçu' : 'payé'}`,
        time: transaction.date ? new Date(transaction.date).toLocaleDateString('fr-FR') : 'Récent',
        status: transaction.type === 'Recette' ? 'success' : 'warning',
        icon: transaction.type === 'Recette' ? <AttachMoney /> : <Warning />,
      })) : [],
    
    // Generate upcoming reservations from real reservation data only
    upcomingReservations: (reservations && Array.isArray(reservations) && reservations.length > 0) ?
      reservations
        .filter(res => new Date(res.dateDepart || res.dateDebut) > new Date())
        .sort((a, b) => new Date(a.dateDepart || a.dateDebut) - new Date(b.dateDepart || b.dateDebut))
        .slice(0, 3)
        .map(res => ({
          id: res.id,
          client: res.clientNom || 'Client',
          destination: res.destination || 'Destination',
          date: new Date(res.dateDepart || res.dateDebut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
          amount: `${(res.prixTotal || 0).toLocaleString()} DZD`,
          status: res.statut === 'Confirmée' ? 'confirmed' : 'pending',
          avatar: null,
        })) : [],
    
    // Generate top destinations from reservations data only
    topDestinations: (reservations && Array.isArray(reservations) && reservations.length > 0) ? 
      Array.from(
        reservations.reduce((acc, res) => {
          const destination = res.destination || 'Inconnue';
          const revenue = res.prixTotal || 0;
          if (!acc.has(destination)) {
            acc.set(destination, { count: 1, revenue });
          } else {
            const current = acc.get(destination);
            acc.set(destination, { 
              count: current.count + 1,
              revenue: current.revenue + revenue
            });
          }
          return acc;
        }, new Map())
      )
      .map(([name, data]) => ({ 
        name, 
        count: data.count,
        revenue: (data.revenue / 1000).toFixed(1), // Convert to thousands
        percentage: reservations.length > 0 ? ((data.count / reservations.length) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5) : [],
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'success': return '#059669';
      case 'warning': return '#d97706';
      case 'error': return '#dc2626';
      case 'info': return '#0284c7';
      default: return '#6366f1';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'reservation': return <Flight sx={{ fontSize: 20 }} />;
      case 'payment': return <AttachMoney sx={{ fontSize: 20 }} />;
      case 'alert': return <Warning sx={{ fontSize: 20 }} />;
      case 'document': return <Description sx={{ fontSize: 20 }} />;
      default: return <Notifications sx={{ fontSize: 20 }} />;
    }
  };

  const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
    <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
          color: gradient ? 'white' : 'inherit',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          } : {},
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mb: 0.5, 
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.875rem' }
              }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' }
              }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ 
                  opacity: gradient ? 0.9 : 0.7, 
                  mt: 0.5,
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip 
                  label={trend} 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: trend.startsWith('+') ? 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                    color: gradient ? 'white' : 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    height: { xs: 20, md: 24 }
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
                color: gradient ? 'white' : 'primary.main',
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 }
              }}
            >
              {icon}
            </Avatar>
          </Box>
          
          {progress && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  Progression
                </Typography>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: gradient ? 'white' : 'primary.main',
                  }
                }} 
              />
            </Box>
          )}
          
          {chart && (
            <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
              {chart}
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );
  
  return (
    <Box sx={{ p: { xs: 0, md: 3 } }} className="space-y-6">
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, md: 4 },
        p: { xs: 2, md: 0 }
      }} className="flex justify-between items-center">
        <div>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            color: 'white',
            mb: 1.5,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: '-0.01em'
          }}>
            Tableau de Bord
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.6)',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
          }}>
            Voici votre vue d'ensemble en temps réel
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          sx={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            },
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)'
          }}
          onClick={() => window.dispatchEvent(new CustomEvent('globalDataRefresh'))}
        >
          Actualiser
        </Button>
      </Box>
      
      {/* Quick Actions Section */}
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RocketLaunch sx={{ mr: 1.5, color: '#6366f1', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Actions Rapides
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)'
                },
                height: '100%'
              }} onClick={() => navigate('/clients')}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{
                      bgcolor: '#6366f1',
                      width: 56,
                      height: 56,
                      mb: 2
                    }}>
                      <Add />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                      Nouveau Client
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Ajouter un client au système
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
                },
                height: '100%'
              }} onClick={() => navigate('/reservations')}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{
                      bgcolor: '#10b981',
                      width: 56,
                      height: 56,
                      mb: 2
                    }}>
                      <Flight />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                      Nouvelle Réservation
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Créer une nouvelle réservation
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)'
                },
                height: '100%'
              }} onClick={() => navigate('/forfaits')}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{
                      bgcolor: '#f59e0b',
                      width: 56,
                      height: 56,
                      mb: 2
                    }}>
                      <CardGiftcard />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                      Nouveau Forfait
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Créer un package de voyage
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)'
                },
                height: '100%'
              }} onClick={() => navigate('/rapports')}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{
                      bgcolor: '#8b5cf6',
                      width: 56,
                      height: 56,
                      mb: 2
                    }}>
                      <BarChart />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                      Analytics
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Analyser les performances
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
      
      {/* Key Metrics Section */}
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesome sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Métriques en Temps Réel
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Row 1: Core Metrics */}
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Clients Actifs</p>
                    <p className="text-2xl font-bold text-white mt-2">{stats.clients}</p>
                    <p className="text-white/60 text-xs mt-1">Base clients totale</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700">
                    <People className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Réservations</p>
                    <p className="text-2xl font-bold text-white mt-2">{stats.reservations}</p>
                    <p className="text-white/60 text-xs mt-1">Total réservations</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-700">
                    <Flight className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Revenus Totaux</p>
                    <p className="text-2xl font-bold text-white mt-2">{(stats.totalRevenue/1000).toFixed(0)}k DA</p>
                    <p className="text-white/60 text-xs mt-1">Chiffre d'affaires</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-700">
                    <AttachMoney className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Bénéfice Net</p>
                    <p className="text-2xl font-bold text-white mt-2">{(stats.netProfit/1000).toFixed(0)}k DA</p>
                    <p className="text-white/60 text-xs mt-1">Profit après dépenses</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-700">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>

          {/* Row 2: Advanced Metrics */}
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Taux de Conversion</p>
                    <p className="text-2xl font-bold text-white mt-2">{stats.conversionRate}%</p>
                    <p className="text-white/60 text-xs mt-1">Confirmées/Totales</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-violet-600 to-violet-700">
                    <Assessment className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Valeur Moy. Réservation</p>
                    <p className="text-2xl font-bold text-white mt-2">{(stats.averageBookingValue/1000).toFixed(1)}k DA</p>
                    <p className="text-white/60 text-xs mt-1">Prix moyen par booking</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-pink-600 to-pink-700">
                    <MonetizationOn className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Forfaits Actifs</p>
                    <p className="text-2xl font-bold text-white mt-2">{stats.packages}</p>
                    <p className="text-white/60 text-xs mt-1">Packages disponibles</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-orange-600 to-orange-700">
                    <CardGiftcard className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            {isLoading ? (
              <SkeletonStat />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Réservations Confirmées</p>
                    <p className="text-2xl font-bold text-white mt-2">{stats.confirmedReservations}</p>
                    <p className="text-white/60 text-xs mt-1">Status confirmé</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-teal-700">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            )}
          </Grid>
        </Grid>
      </Box>
      
      {/* Main Dashboard Content */}
      <Grid 
        container 
        spacing={{ xs: 2, md: 3 }} 
        sx={{ 
          px: { xs: 2, md: 0 },
          // Better spacing for mobile
          '& > .MuiGrid-item': {
            pb: { xs: 2, md: 3 }
          }
        }}
      >
        {/* Recent Activity */}
        <Grid item xs={12} md={6} lg={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-full"
          >
            <CardContent sx={{ 
              p: { xs: 2, md: 3 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: { xs: 1.5, md: 2 }
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  color: 'white'
                }}>
                  Activité Récente
                </Typography>
                <Chip 
                  label="Aujourd'hui" 
                  size="small" 
                  sx={{ 
                    fontWeight: 500,
                    height: { xs: '24px', md: '24px' },
                    '& .MuiChip-label': {
                      px: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.7rem', md: '0.75rem' }
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </Box>
              
              {(stats.recentActivity && Array.isArray(stats.recentActivity) && stats.recentActivity.length > 0) ? (
                <List sx={{ p: 0 }}>
                {stats.recentActivity.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        px: 0, 
                        py: 1.5,
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Avatar
                          sx={{
                            bgcolor: `${getStatutColor(activity.status)}40`,
                            color: 'white',
                            width: 36,
                            height: 36
                          }}
                        >
                          {getNotificationIcon(activity.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" component="span" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                            {activity.title}
                          </Typography>
                        }
                        secondary={
                          <span>
                            <Typography variant="body2" component="span" sx={{ mb: 0.5, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              Il y a {activity.time}
                            </Typography>
                          </span>
                        }
                      />
                    </ListItem>
                    {index < stats.recentActivity.length - 1 && (
                      <Divider component="li" sx={{ ml: 5, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Aucune activité récente
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 1, display: 'block' }}>
                    Les nouvelles transactions apparaîtront ici
                  </Typography>
                </Box>
              )}
              
              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Voir toutes les activités
              </Button>
            </CardContent>
          </motion.div>
        </Grid>
        
        {/* Upcoming Reservations */}
        <Grid item xs={12} md={6} lg={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-full"
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' }, color: 'white' }}>Réservations à venir</Typography>
                <IconButton size="small" sx={{ color: 'white' }}>
                  <MoreVert fontSize="small" />
                </IconButton>
              </Box>
              
              {(stats.upcomingReservations && Array.isArray(stats.upcomingReservations) && stats.upcomingReservations.length > 0) ? (
                <List sx={{ p: 0 }}>
                {stats.upcomingReservations.map((reservation, index) => (
                  <React.Fragment key={reservation.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        px: 0, 
                        py: 1.5,
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Avatar sx={{ 
                          bgcolor: reservation.status === 'confirmed' ? '#10b981' : '#f59e0b',
                          color: 'white',
                          width: 36,
                          height: 36
                        }}>
                          {reservation.avatar || reservation.client.charAt(0)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: 'white' }}>
                              {reservation.client}
                            </Typography>
                                                          <Chip 
                              label={reservation.status === 'confirmed' ? 'Confirmé' : 'En attente'} 
                              size="small"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                backgroundColor: reservation.status === 'confirmed' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                                color: reservation.status === 'confirmed' ? '#10b981' : '#f59e0b',
                                border: 'none'
                              }}
                            />
                          </span>
                        }
                        secondary={
                          <span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                              <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" component="span" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {reservation.destination}
                              </Typography>
                            </span>
                            <span style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Today sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                  {reservation.date}
                                </Typography>
                              </span>
                              <Typography variant="caption" component="span" sx={{ fontWeight: 600, color: 'white' }}>
                                {reservation.amount}
                              </Typography>
                            </span>
                          </span>
                        }
                      />
                    </ListItem>
                    {index < stats.upcomingReservations.length - 1 && (
                      <Divider component="li" sx={{ ml: 5, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Aucune réservation à venir
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 1, display: 'block' }}>
                    Les prochaines réservations apparaîtront ici
                  </Typography>
                </Box>
              )}
              
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669, #047857)'
                  },
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
                }}
              >
                Gérer les réservations
              </Button>
            </CardContent>
          </motion.div>
        </Grid>
        
        {/* Top Destinations */}
        <Grid item xs={12} md={6} lg={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-full"
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' }, color: 'white' }}>Destinations Populaires</Typography>
                <Chip 
                  label="Ce mois" 
                  size="small" 
                  sx={{ 
                    fontWeight: 500,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </Box>
              
              {(stats.topDestinations && Array.isArray(stats.topDestinations) && stats.topDestinations.length > 0) ? (
                <List sx={{ p: 0 }}>
                {stats.topDestinations.map((destination, index) => (
                  <ListItem 
                    key={destination.name}
                    sx={{ 
                      px: 0, 
                      py: 1.5,
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.02)',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 42 }}>
                      <Avatar sx={{ 
                        bgcolor: index === 0 ? 'rgba(245, 158, 11, 0.8)' : 
                                index === 1 ? 'rgba(99, 102, 241, 0.8)' : 
                                index === 2 ? 'rgba(16, 185, 129, 0.8)' : 'rgba(139, 92, 246, 0.8)',
                        color: 'white',
                        width: 36,
                        height: 36
                      }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                                          <ListItemText
                        primary={
                          <Typography variant="body1" component="span" sx={{ fontWeight: 600, color: 'white' }}>
                            {destination.name}
                          </Typography>
                        }
                        secondary={
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Group sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2" component="span" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {destination.count} réservations
                            </Typography>
                          </span>
                        }
                      />
                  </ListItem>
                ))}
              </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Aucune destination disponible
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 1, display: 'block' }}>
                    Les destinations populaires apparaîtront ici
                  </Typography>
                </Box>
              )}
              
              <Button 
                variant="outlined" 
                fullWidth 
                endIcon={<ArrowForward />}
                sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Voir toutes les destinations
              </Button>
            </CardContent>
          </motion.div>
        </Grid>

        {/* Analytics Charts Section */}
        {/* Monthly Reservations Trend Chart */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-full"
          >
            <CardContent sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                  Évolution des Réservations
                </Typography>
                <Chip 
                  label="6 derniers mois" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(59, 130, 246, 0.2)',
                    color: '#60a5fa',
                    fontWeight: 500 
                  }} 
                />
              </Box>
              
              {stats.monthlyReservations.length > 0 ? (
                <Box sx={{ height: 200, width: '100%', display: 'flex', alignItems: 'end', justifyContent: 'space-around', mt: 2 }}>
                  {stats.monthlyReservations.map((item, index) => (
                    <Box key={index} sx={{ textAlign: 'center', flex: 1 }}>
                      <Box
                        sx={{
                          height: `${Math.max((item.count / Math.max(...stats.monthlyReservations.map(r => r.count), 1)) * 140, 10)}px`,
                          bgcolor: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                          borderRadius: '6px 6px 0 0',
                          mb: 1,
                          mx: 0.5,
                          position: 'relative',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s'
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem' }}>
                        {item.month}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#60a5fa', fontWeight: 600, fontSize: '0.8rem' }}>
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ShowChart sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Pas de données disponibles
                  </Typography>
                </Box>
              )}
            </CardContent>
          </motion.div>
        </Grid>

        {/* Monthly Revenue Trend Chart */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-full"
          >
            <CardContent sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                  Évolution du Chiffre d'Affaires
                </Typography>
                <Chip 
                  label="En milliers DA" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(34, 197, 94, 0.2)',
                    color: '#4ade80',
                    fontWeight: 500 
                  }} 
                />
              </Box>
              
              {stats.monthlyRevenue.length > 0 ? (
                <Box sx={{ height: 200, width: '100%', display: 'flex', alignItems: 'end', justifyContent: 'space-around', mt: 2 }}>
                  {stats.monthlyRevenue.map((item, index) => (
                    <Box key={index} sx={{ textAlign: 'center', flex: 1 }}>
                      <Box
                        sx={{
                          height: `${Math.max((item.revenue / Math.max(...stats.monthlyRevenue.map(r => r.revenue), 1)) * 140, 10)}px`,
                          bgcolor: 'linear-gradient(135deg, #10b981, #059669)',
                          background: 'linear-gradient(135deg, #10b981, #059669)',
                          borderRadius: '6px 6px 0 0',
                          mb: 1,
                          mx: 0.5,
                          position: 'relative',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s'
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem' }}>
                        {item.month}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4ade80', fontWeight: 600, fontSize: '0.8rem' }}>
                        {item.revenue.toFixed(0)}k
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BarChart sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Pas de données disponibles
                  </Typography>
                </Box>
              )}
            </CardContent>
          </motion.div>
        </Grid>

        {/* Package Performance Chart */}
        <Grid item xs={12} md={8}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-full"
          >
            <CardContent sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                  Performance des Forfaits
                </Typography>
                <Chip 
                  label="Top 5" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(168, 85, 247, 0.2)',
                    color: '#c084fc',
                    fontWeight: 500 
                  }} 
                />
              </Box>
              
              {stats.packagePerformance.length > 0 ? (
                <Box sx={{ space: 2 }}>
                  {stats.packagePerformance.map((pkg, index) => (
                    <Box key={index} sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                          {pkg.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Chip
                            label={`${pkg.bookings} réservations`}
                            size="small"
                            sx={{ bgcolor: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa' }}
                          />
                          <Chip
                            label={`${pkg.revenue.toFixed(0)}k DA`}
                            size="small"
                            sx={{ bgcolor: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' }}
                          />
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max((pkg.bookings / Math.max(...stats.packagePerformance.map(p => p.bookings), 1)) * 100, 5)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: `linear-gradient(90deg, ${['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index % 5]}, ${['#1d4ed8', '#059669', '#d97706', '#dc2626', '#7c3aed'][index % 5]})`
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <PieChart sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Aucune donnée de performance disponible
                  </Typography>
                </Box>
              )}
            </CardContent>
          </motion.div>
        </Grid>

        {/* Financial Summary */}
        <Grid item xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-full"
          >
            <CardContent sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 3 }}>
                Résumé Financier
              </Typography>
              
              <Box sx={{ space: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(34, 197, 94, 0.1)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUp sx={{ color: '#4ade80', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Revenus Totaux
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#4ade80', fontWeight: 700 }}>
                    {(stats.totalRevenue/1000).toFixed(0)}k DA
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(239, 68, 68, 0.1)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingDown sx={{ color: '#f87171', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Dépenses Totales
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#f87171', fontWeight: 700 }}>
                    {(stats.totalExpenses/1000).toFixed(0)}k DA
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: 2, bgcolor: stats.netProfit >= 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccountBalance sx={{ color: stats.netProfit >= 0 ? '#4ade80' : '#f87171', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Bénéfice Net
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ color: stats.netProfit >= 0 ? '#4ade80' : '#f87171', fontWeight: 700 }}>
                    {(stats.netProfit/1000).toFixed(0)}k DA
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(59, 130, 246, 0.1)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                    Indicateurs Clés
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Taux de conversion
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#60a5fa', fontWeight: 600 }}>
                      {stats.conversionRate}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Valeur moy. réservation
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#60a5fa', fontWeight: 600 }}>
                      {(stats.averageBookingValue/1000).toFixed(1)}k DA
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 